import type { AxiosProgressEvent } from "axios";
import { api } from "../utils/axios";
import type { RestaurantFormData, Restaurant, FormReviewData, ReviewResponse } from "../../Body/Manager/restaurant.types";
import { normalizeRestaurantPayload } from "../utils/restaurantPayload";

export const createRestaurant = async (formData: Pick<RestaurantFormData, "name" | "description" | "location">) => {
  try {
    const response = await api.post("/api/restaurants", formData);

    return normalizeRestaurantPayload(response.data.restaurant);
  } catch (error: any) {
    console.error("Error creating restaurant:", error);
    throw new Error(error.response?.data?.message || "Failed to create restaurant");
  }
};

export const updateRestaurant = async (
  restaurantId: string,
  updatedData: Partial<RestaurantFormData>
) => {
  try {
    if (!restaurantId) {
      throw new Error("Missing restaurant ID for update");
    }

    const response = await api.put(`/api/restaurants/private/${restaurantId}`, updatedData);

    // Return updated restaurant object (depends on backend response)
    return normalizeRestaurantPayload(response.data.restaurant || response.data);
  } catch (error: any) {
    console.error("❌ Error updating restaurant:", error);
    throw new Error(error.response?.data?.message || "Failed to update restaurant");
  }
};

export const publishMenu = async (
  restaurantId: string,
  orderedImageUrls: string[],
) => {
  try {
    if (!restaurantId) {
      throw new Error("Missing restaurant ID for update");
    }

    const response = await api.post(`/api/restaurants/${restaurantId}/publish-menu`, {
      orderedImageUrls,
    });
    // Return updated restaurant object (depends on backend response)
    return response.data.restaurant || response.data;
  } catch (error: any) {
    console.error("❌ Error: failed to publish menu, you may check connection", error);
    throw new Error(error.response?.data?.message || "Failed to publish menu, you may check connection");
  }
};
// to user
export const getUserRestaurants = async () => {
  try {
    const response = await api.get("/api/restaurants/userrests");

    return normalizeRestaurantPayload(response.data.restaurants); // return just the array
  } catch (error: any) {
    console.error("Error fetching restaurants:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch restaurants");
  }
};

export const getRestaurantMenu = async (restaurantId) => {
  try {
    const response = await api.get(`/api/menu/${restaurantId}`);
    return response.data // return just the array
  } catch (error: any) {
    console.error("Error fetching restaurants:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch restaurants");
  }
};

export const approveRestaurant = async (id: string) => {
  try {
    if (!id) {
      throw new Error("Missing restaurant ID");
    }

    const response = await api.put(
      `/api/restaurants/private/${id}/approve`,
      {},
      {
        withCredentials: true,
      }
    );

    // Return backend response (depending on your API’s shape)
    return normalizeRestaurantPayload(response.data.restaurant || response.data);
  } catch (error: any) {
    console.error("❌ Error approving restaurant:", error);

    // Extract message from backend or provide fallback
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to approve restaurant";

    throw new Error(message);
  }
};
export const archiveRestaurant = async (id: string) => {
  try {
    if (!id) {
      throw new Error("Missing restaurant ID for archive");
    }

    const response = await api.put(
      `/api/restaurants/private/${id}/archive`,
      {},
      {
        withCredentials: true,
      }
    );

    // Return backend response (depending on your API’s shape)
    return normalizeRestaurantPayload(response.data.restaurant || response.data);
  } catch (error: any) {
    console.error("❌ Error archiving restaurant:", error);

    // Extract message from backend or provide fallback
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to archive restaurant";

    throw new Error(message);
  }
};

export const deleteRestaurant = async (id: string) => {
  try {
    const response = await api.delete(`/api/restaurants/private/${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error deleting restaurant:", error);
    throw new Error(error.response?.data?.message || "Failed to delete restaurant");
  }
};

export const uploadFile = async ( file: File, onProgress?: (progress: number) => void): Promise<{ url: string; publicId: string }> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post("/api/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
      onUploadProgress(progressEvent: AxiosProgressEvent) {
        if (onProgress && progressEvent.total) {
          const percentaCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentaCompleted)
        }
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("❌ Upload failed:", error);
    throw new Error(error.response?.data?.message || "File upload failed");
  }
}

export const uploadFiles = async (
  files: File[],
  onProgress?: (fileName: string, progress: number) => void
): Promise<{ url: string; publicId: string }[]> => {
  const promises = files.map((file) =>
    uploadFile(file, (progress) => onProgress?.(file.name, progress))
  );
  return Promise.all(promises);
};

export const removeRestaurantFile = async (
  restaurantId: string,
  category: "cover" | "menu" | "food" | "ambiance",
  index?: number,
  foodId?: string
) => {
  const params = new URLSearchParams();
  if (index !== undefined) params.append("index", index.toString());
  if (foodId) params.append("foodId", foodId);

  try {
    const response = await api.delete(
      `/api/restaurants/private/${restaurantId}/gallery/${category}?${params.toString()}`,
      { withCredentials: true }
    );
    return response.data.gallery;
  } catch (error: any) {
    console.error("❌ Error deleting image:", error);
    throw new Error(error.response?.data?.message || "Failed to remove image");
  }
};

export const fetchExploreSections = async (endpoint: string) => {
  try {
    const response = await api.get(endpoint, {
      withCredentials: true
    });
    return normalizeRestaurantPayload(response.data.restaurants);
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to load restaurants");
  }
};

export const fetchRestaurantById = async (id: string): Promise<Restaurant | null> => {
  try {
    const response = await api.get(`/api/restaurants/${id}`, { withCredentials: true });
    return normalizeRestaurantPayload(response.data.restaurant) as Restaurant;
  } catch (error) {
    console.error("Error fetching restaurant:", error);
    return null;
  }
};

export const fetchSecureRestaurantById = async (id: string): Promise<Restaurant | null> => {
  try {
    const response = await api.get(`/api/restaurants/private/${id}/secure`, { withCredentials: true });
    return normalizeRestaurantPayload(response.data.restaurant) as Restaurant;
  } catch (error) {
    console.error("Error fetching restaurant:", error);
    throw new Error(error.response?.data?.message || "You do not have access to this restaurant");
  }
};

export const increaseViewCount = async (restaurantId: string) => {
  try {
    await api.post(`/api/restaurants/${restaurantId}/views`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
  } catch (error: any) {
    console.error("Error increasing view count:", error);
    return;
  }
};
export const toggleSaveRestaurant = async (restaurantId: string) => {
  try {
   const response = await api.post<{ saved: boolean }>(
    `/api/users/${restaurantId}/save`,
    {}, // no body needed
    {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, 
    } )
    
    return response.data.saved; 
  } catch (error) {
    console.error(error)
  }
};

export const submitReview = async (
  restaurantId: string,
  reviewData: FormReviewData
): Promise<ReviewResponse> => {
  try {
    const response = await api.post<ReviewResponse>(
      `/api/restaurants/reviews/${restaurantId}`,
      reviewData,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true, // if you are using cookies for auth
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Error submitting review:", error);
    const message =
      error.response?.data?.message || "Failed to submit review";
    throw new Error(message);
  }
};

export const getRestaurantReviews = async (restaurantId: string) => {
  try {
    const response = await api.get(`/api/restaurants/${restaurantId}/reviews`, {
      withCredentials: true,
    });
    return response.data.reviews; // Array of review objects
  } catch (error: any) {
    console.error("Error fetching reviews:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch reviews");
  }
};

export async function fetchSuggestions(params: {
  q: string;
  filters?: string[];
  lat?: number;
  lng?: number;
  limit?: number;
}): Promise<{ words: string[]; restaurants: Restaurant[] }> {
  const query = new URLSearchParams({
    q: params.q,
    ...(params.filters ? { filters: params.filters.join(",") } : {}),
    ...(params.lat ? { lat: params.lat.toString() } : {}),
    ...(params.lng ? { lng: params.lng.toString() } : {}),
    limit: (params.limit ?? 5).toString(),
  });

  const res = await api.get(`/api/restaurants/search/suggestions?${query.toString()}`);
  return {
    words: res.data.data.suggestions,
    restaurants: normalizeRestaurantPayload(res.data.data.restaurants),
  };
}

export async function performSmartSearch(params: { 
  q: string;
  filters?: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
}): Promise<{
  restaurants: Restaurant[];
  total: number;
  page: number;
  message?: string;
  totalPages: number;
}> {
  try {
    const query = new URLSearchParams({
      q: params.q,
      ...(params.filters ? { filters: params.filters.join(",") } : {}),
      page: (params.page ?? 1).toString(),
      limit: (params.limit ?? 20).toString(),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
    });

    const response = await api.get(`/api/restaurants/smart-search?${query.toString()}`);

    // Optional: Validate the structure of the response
    if (!response.data || !response.data.restaurants) {
      throw new Error("Unexpected response structure from smart search API");
    }

    return {
      restaurants: normalizeRestaurantPayload(response.data.restaurants),
      total: response.data.total,
      page: response.data.page,
      totalPages: response.data.totalPages,
    };
  } catch (error: any) {
    // Throw a more descriptive error
    throw new Error(`Smart search failed: ${error.message || error}`);
  }
}

export const searchNearbyRestaurants = async (
  lat: number,
  lng: number,
  radius: number = 50000,
  page: number = 1,
  limit: number = 20
) => {
  try {
    const response = await api.get("/api/restaurants/nearby", {
      params: {
        lat,
        lng,
        radius,
        page,
        limit,
      },
      withCredentials: true,
    });
    return normalizeRestaurantPayload(response.data);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 
      "Failed to load nearby restaurants"
    );
  }
};

export async function AIText(data: {
  type: "description" | "review";
  name?: string;
  cuisine?: string[] | string;
  typeOfFood?: string[] | string;
  experience?: string[] | string;
  tags?: string[] | string;

  // review fields
  enjoyed?: string;
  improve?: string;
  recommend?: string;
}): Promise<{ text: string }> {
  try {
    const response = await api.post(`/api/restaurants/ai/text`, data);

    if (!response.data || typeof response.data.text !== "string") {
      throw new Error("Invalid AI response format");
    }

    return { text: response.data.text };
  } catch (error: any) {
    throw new Error(`AI text generation failed: ${error.message || error}`);
  }
}

